import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Mail, Lock, MailCheck } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';
import useSEO from '../hooks/useSEO';
import './AuthPage.css';

const IS_DEV = process.env.REACT_APP_SHOW_DEMO === 'true';

const DEMO = IS_DEV ? [
  { label: 'Admin',    email: 'admin@karyainspirasi.id',    pw: 'admin123',    role: 'admin' },
  { label: 'Editor',   email: 'editor@karyainspirasi.id',   pw: 'editor123',   role: 'editor' },
  { label: 'Producer', email: 'producer@karyainspirasi.id', pw: 'producer123', role: 'producer' },
  { label: 'Reader',   email: 'reader@karyainspirasi.id',   pw: 'reader123',   role: 'reader' },
] : [];

const ROLE_DEST = { reader: '/', editor: '/editor', producer: '/producer', admin: '/admin' };

export default function LoginPage() {
  const { settings } = useSiteSettings();
  const sn = settings.siteName;
  useSEO({ title: 'Masuk', siteName: sn, noindex: true });
  const { login, verifyOtp, googleSignIn, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname;
  const googleBtnRef = useRef(null);
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState('');

  useEffect(() => {
    if (user) navigate(from || ROLE_DEST[user.role] || '/', { replace: true });
  }, [user]);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || !window.google) return;
    window.google.accounts.id.initialize({ client_id: clientId, callback: handleGoogleCallback, auto_select: false });
    if (googleBtnRef.current) {
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'outline', size: 'large', shape: 'rectangular', width: '100%',
        text: 'signin_with', logo_alignment: 'center',
      });
    }
  }, []);

  const handleGoogleCallback = async (response) => {
    setLoading(true);
    try {
      const user = await googleSignIn(response.credential);
      toast.success(`Selamat datang, ${user.name}!`);
      navigate(ROLE_DEST[user.role] || '/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Google Sign-In gagal');
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerification(false);
    try {
      const res = await login(form.email, form.password, rememberMe);
      if (res.needsOtp) {
        setOtpStep(true);
        toast.success('Kode verifikasi dikirim ke email Anda.');
        return;
      }
      toast.success(`Selamat datang, ${res.user.name}!`);
      navigate(from || ROLE_DEST[res.user.role] || '/dashboard', { replace: true });
    } catch (err) {
      if (err.response?.data?.needsVerification) {
        setNeedsVerification(true);
      } else {
        toast.error(err.response?.data?.message || 'Email atau password salah.');
      }
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.trim().length < 6) { toast.error('Masukkan 6 digit kode.'); return; }
    setLoading(true);
    try {
      const u = await verifyOtp(form.email, otp.trim(), rememberMe);
      toast.success(`Selamat datang, ${u.name}!`);
      navigate(from || ROLE_DEST[u.role] || '/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Kode salah atau kedaluwarsa.');
    } finally { setLoading(false); }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      await login(form.email, form.password, rememberMe);
      toast.success('Kode baru telah dikirim.');
    } catch { toast.error('Gagal mengirim ulang kode.'); }
    finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      // Login dulu untuk dapat token sementara, lalu resend
      const res = await API.post('/auth/resend-verification-public', { email: form.email });
      toast.success(res.data.message || 'Email verifikasi telah dikirim ulang.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal mengirim ulang. Coba beberapa saat lagi.');
    } finally { setResending(false); }
  };

  const quickLogin = async (d) => {
    setLoading(true);
    try {
      const user = await login(d.email, d.pw);
      toast.success(`Login sebagai ${user.name}`);
      navigate(ROLE_DEST[user.role] || '/dashboard', { replace: true });
    } catch {
      toast.error('Pastikan sudah menjalankan: cd backend && npm run seed');
    } finally { setLoading(false); }
  };

  return (
    <div className="auth-root">
      {/* Top bar */}
      <div className="auth-topbar">
        <Link to="/" className="auth-logo" style={{ marginBottom: 0 }}>
          <img
            src={settings.logoAuthUrl || settings.logoUrl || '/logo-inspira.png'} alt={sn}
            style={{ height: 32, objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
          <span style={{ display: 'none', alignItems: 'center', gap: 6 }}>
            <span className="auth-logo-icon">IN</span>
            {sn}
          </span>
        </Link>
      </div>

      <div className="auth-body">
        <div className="auth-card">
          <Link to="/" className="auth-logo">
            <img
              src="/logo-inspira.png" alt="Inspira"
              style={{ height: 36, objectFit: 'contain' }}
              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
            <span style={{ display: 'none', alignItems: 'center', gap: 6 }}>
              <span className="auth-logo-icon">IN</span>
              {sn}
            </span>
          </Link>

          <h1 className="auth-title">Masuk ke Akun</h1>
          <p className="auth-sub">Platform dokumentasi karya inovasi & pengabdian</p>

          {needsVerification && (
            <div style={{
              background: '#FFF7ED', border: '1px solid #FED7AA',
              borderRadius: 10, padding: '14px 16px', marginBottom: 16,
              display: 'flex', gap: 12, alignItems: 'flex-start'
            }}>
              <MailCheck size={18} style={{ color: '#D4690A', flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#92400E', marginBottom: 4 }}>
                  Email belum diverifikasi
                </div>
                <div style={{ fontSize: 13, color: '#78350F', lineHeight: 1.6, marginBottom: 10 }}>
                  Cek inbox <strong>{form.email}</strong> dan klik link verifikasi yang kami kirim saat registrasi.
                </div>
                <button
                  onClick={handleResend}
                  disabled={resending}
                  className="btn btn-primary btn-sm"
                >
                  {resending ? 'Mengirim...' : 'Kirim Ulang Email Verifikasi'}
                </button>
              </div>
            </div>
          )}

          {process.env.REACT_APP_GOOGLE_CLIENT_ID ? (
            <>
              <div ref={googleBtnRef} className="google-btn-wrap" />
              <div className="auth-divider"><span>atau masuk dengan email</span></div>
            </>
          ) : (
            <div className="auth-divider"><span>masuk dengan email</span></div>
          )}

          {otpStep ? (
            <form onSubmit={handleVerifyOtp}>
              <p style={{ fontSize: 13, color: 'var(--in-text-muted)', marginBottom: 16, lineHeight: 1.6 }}>
                Kami mengirim kode 6 digit ke <strong>{form.email}</strong>. Masukkan kode untuk menyelesaikan login.
              </p>
              <div className="form-group">
                <label className="form-label">Kode Verifikasi</label>
                <input
                  type="text" inputMode="numeric" maxLength={6}
                  className="form-input"
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="______"
                  style={{ letterSpacing: 8, fontSize: 20, fontWeight: 700, textAlign: 'center' }}
                  autoFocus required
                />
              </div>
              <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading}>
                {loading ? 'Memverifikasi...' : 'Verifikasi & Masuk'}
              </button>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 13 }}>
                <button type="button" className="auth-link" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} onClick={() => { setOtpStep(false); setOtp(''); }}>← Kembali</button>
                <button type="button" className="auth-link" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} onClick={handleResendOtp} disabled={loading}>Kirim ulang kode</button>
              </div>
            </form>
          ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <div className="input-icon-wrap">
                <Mail size={15} className="input-icon" />
                <input
                  type="email" className="form-input input-with-icon"
                  value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="email@domain.com" required autoFocus
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Kata Sandi</label>
              <div className="input-icon-wrap">
                <Lock size={15} className="input-icon" />
                <input
                  type={showPw ? 'text' : 'password'}
                  className="form-input input-with-icon input-with-action"
                  value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="Minimal 6 karakter" required
                />
                <button type="button" className="input-action" onClick={() => setShowPw(!showPw)}>
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '12px 0 4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  style={{ width: 15, height: 15, accentColor: 'var(--in-blue)', cursor: 'pointer' }}
                />
                <label htmlFor="rememberMe" style={{ fontSize: 13, color: 'var(--in-text-muted)', cursor: 'pointer', userSelect: 'none' }}>
                  Tetap masuk selama 30 hari
                </label>
              </div>
              <Link to="/lupa-password" className="auth-link" style={{ fontSize: 13 }}>Lupa sandi?</Link>
            </div>
            <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading} style={{ marginTop: 4 }}>
              {loading ? 'Memproses...' : 'Masuk'}
            </button>
          </form>
          )}

          <p className="auth-footer-text">
            Belum punya akun? <Link to="/register" className="auth-link">Daftar gratis</Link>
          </p>

          {IS_DEV && DEMO.length > 0 && (
            <div className="demo-section">
              <div className="demo-label">Akun Demo (jalankan npm run seed dulu):</div>
              <div className="demo-grid">
                {DEMO.map(d => (
                  <button key={d.role} className="demo-btn" onClick={() => quickLogin(d)} disabled={loading}>
                    <span className={`demo-role-dot role-${d.role}`} />
                    {d.label}
                  </button>
                ))}
              </div>
              <p style={{ fontSize: 11, color: 'var(--in-text-faint)', marginTop: 10, textAlign: 'center', lineHeight: 1.5 }}>
                Registrasi publik hanya untuk role <strong>Reader</strong>.<br />
                Role Editor, Producer, dan Admin diassign oleh Admin.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="auth-footer">
        <p>© {new Date().getFullYear()} {sn} · {settings.copyrightText}</p>
        <div style={{ display: 'flex', gap: 16 }}>
          <a href="https://inspiratekno.com" target="_blank" rel="noopener noreferrer">inspiratekno.com</a>
          <a href="https://lms.inspiratekno.com" target="_blank" rel="noopener noreferrer">InspiraLearn</a>
        </div>
      </div>
    </div>
  );
}
